import React from "react";
import {X} from "lucide-react";

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose max-w-none">
            <p className="text-sm text-gray-600 mb-6">
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Information We Collect
            </h3>
            <p className="text-gray-700 mb-4">
              We collect information you provide directly to us, such as when
              you:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Subscribe to our newsletter</li>
              <li>Purchase books through our online store</li>
              <li>Contact us with questions or feedback</li>
              <li>Create an account on our website</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Personal Information
            </h3>
            <p className="text-gray-700 mb-4">
              The types of personal information we may collect include:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Name and email address (for newsletter subscriptions)</li>
              <li>Shipping and billing addresses (for book purchases)</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              How We Use Your Information
            </h3>
            <p className="text-gray-700 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Send you newsletters and updates about new book releases</li>
              <li>Process and fulfill your book orders</li>
              <li>Provide customer support</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Information Sharing
            </h3>
            <p className="text-gray-700 mb-6">
              We do not sell, trade, or otherwise transfer your personal
              information to third parties, except:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>
                To trusted service providers who assist us in operating our
                website and conducting business (such as payment processors and
                shipping companies)
              </li>
              <li>When required by law or to protect our rights</li>
              <li>With your explicit consent</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Data Security
            </h3>
            <p className="text-gray-700 mb-6">
              We implement appropriate security measures to protect your
              personal information against unauthorized access, alteration,
              disclosure, or destruction. All payment information is processed
              securely through Stripe and is not stored on our servers.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Newsletter and Communications
            </h3>
            <p className="text-gray-700 mb-6">
              If you subscribe to our newsletter, you will receive periodic
              emails about new book releases, author updates, and special
              offers. You can unsubscribe at any time by clicking the
              unsubscribe link in any email or by contacting us directly.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Cookies and Tracking
            </h3>
            <p className="text-gray-700 mb-6">
              Our website may use cookies and similar tracking technologies to
              enhance your browsing experience, analyze site traffic, and
              personalize content. You can control cookie settings through your
              browser preferences.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Your Rights
            </h3>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Access and update your personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of marketing communications</li>
              <li>Request a copy of your data</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Children's Privacy
            </h3>
            <p className="text-gray-700 mb-6">
              Our services are not directed to children under 13. We do not
              knowingly collect personal information from children under 13. If
              we become aware that we have collected personal information from a
              child under 13, we will take steps to delete such information.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Changes to This Policy
            </h3>
            <p className="text-gray-700 mb-6">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Last updated" date. Your continued use
              of our services after any modifications constitutes acceptance of
              the updated policy.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Us
            </h3>
            <p className="text-gray-700 mb-6">
              If you have any questions about this Privacy Policy or our data
              practices, please contact us at:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">
                <strong>Hallie Parker Writes</strong>
                <br />
                Email: privacy@hallieparkerwrites.com
                <br />
                Website: www.hallieparkerwrites.com
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button onClick={onClose} className="w-full btn btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
